import React, { useEffect, useState } from "react";
import { GitBranch, AlertTriangle, CheckCircle2, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { workflowRules, workflowTransition, workflowValidate, operasyonHub } from "../lib/api";

const STATUS_OPTIONS = [
    { value: "YENI", label: "Yeni" },
    { value: "BASVURU", label: "Başvuru" },
    { value: "TAHKIM", label: "Tahkime Başvuru" },
    { value: "KAPATILDI", label: "Kapatıldı" },
];

export default function WorkflowPanel({ dosyaId, currentStatus, onTransition }) {
    const [rules, setRules] = useState([]);
    const [target, setTarget] = useState("");
    const [validation, setValidation] = useState(null);
    const [loading, setLoading] = useState(false);

    const [pilot, setPilot] = useState(null);

    useEffect(() => {
        workflowRules()
            .then((d) => setRules(d.rules || []))
            .catch(() => {});
    }, []);

    const applicable = rules.filter((r) => r.from === currentStatus);

    async function checkStatus(next) {
        setTarget(next);
        if (!next || next === currentStatus) {
            setValidation(null);
            return;
        }
        setLoading(true);
        setPilot(null);
        try {
            const res = await workflowValidate({
                dosya_id: dosyaId,
                new_status: next,
                old_status: currentStatus,
            });
            setValidation(res);
            try {
                const pilotRes = await operasyonHub.workflowPilot({
                    dosya_id: dosyaId,
                    hedef_durum: next,
                    uygula: false,
                });
                if (pilotRes?.checklist) setPilot(pilotRes);
            } catch {
                /* pilot opsiyonel */
            }
        } catch (e) {
            setValidation({
                ok: false,
                errors: [e?.response?.data?.detail || "Doğrulama hatası"],
            });
        } finally {
            setLoading(false);
        }
    }

    async function applyTransition(force = false) {
        if (!target || target === currentStatus) return;
        setLoading(true);
        try {
            const res = await workflowTransition({
                dosya_id: dosyaId,
                new_status: target,
                force,
                whatsapp_gonder: true,
            });
            if (res.whatsapp?.ok) {
                toast.success("Durum güncellendi · WhatsApp müvekkile otomatik gönderildi.");
            } else if (res.whatsapp && !res.whatsapp.ok) {
                toast.success("Durum güncellendi.");
                toast.warning(res.whatsapp.detail || "WhatsApp gönderilemedi.");
            } else {
                toast.success("Durum güncellendi.");
            }
            setValidation(null);
            setTarget("");
            onTransition?.();
        } catch (e) {
            const data = e?.response?.data || {};
            const wf = data.workflow;
            const rawErrs = wf?.errors || data.errors;
            const errs = Array.isArray(rawErrs)
                ? rawErrs
                : rawErrs
                  ? [rawErrs]
                  : [data.detail || "Geçiş reddedildi"];
            setValidation(
                wf || {
                    ok: false,
                    errors: errs,
                    missing_evrak: wf?.missing_evrak,
                },
            );
            toast.error(Array.isArray(errs) ? errs[0] : errs);
        } finally {
            setLoading(false);
        }
    }

    const currentLabel =
        STATUS_OPTIONS.find((s) => s.value === currentStatus)?.label || currentStatus;

    const waPreview = validation?.whatsapp;

    return (
        <section className="rounded-xl border border-[#D4AF37]/25 bg-[#121A2F]/80 p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
                <GitBranch className="h-5 w-5 text-[#D4AF37]" />
                <h3 className="font-display text-lg text-white">Ana İş Akışı</h3>
            </div>
            <p className="text-xs text-slate-500 mb-3">
                Geçerli durum: <span className="text-slate-300">{currentLabel}</span>
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
                {applicable.map((r) => (
                    <button
                        key={r.to}
                        type="button"
                        onClick={() => checkStatus(r.to)}
                        className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${
                            target === r.to
                                ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F3E5AB]"
                                : "border-white/10 text-slate-400 hover:border-white/20"
                        }`}
                    >
                        → {r.to_label}
                    </button>
                ))}
            </div>
            {loading && (
                <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Kontrol ediliyor…
                </div>
            )}
            {target && (pilot?.checklist?.length || validation) ? (
                <div className="rounded-lg border border-white/10 bg-black/20 p-3 mb-3 space-y-2">
                    <p className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">
                        Geçiş pilotu
                    </p>
                    {(pilot?.checklist || []).map((line, i) => (
                        <p key={i} className="text-xs text-slate-400">
                            • {line}
                        </p>
                    ))}
                    {validation?.warnings?.map((w, i) => (
                        <p key={`w-${i}`} className="text-xs text-amber-300/90">
                            Uyarı: {w}
                        </p>
                    ))}
                </div>
            ) : null}
            {validation && !validation.ok && (
                <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 mb-3 text-sm">
                    <div className="flex items-start gap-2 text-rose-300">
                        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                        <ul className="list-disc pl-4 space-y-1">
                            {(validation.errors || []).map((e, i) => (
                                <li key={i}>{e}</li>
                            ))}
                        </ul>
                    </div>
                    {validation.missing_evrak?.length > 0 && (
                        <p className="text-xs text-slate-400 mt-2">
                            Eksik evrak:{" "}
                            {validation.missing_evrak.map((e) => e.name).join(", ")}
                        </p>
                    )}
                </div>
            )}
            {validation?.ok && target && target !== currentStatus && (
                <>
                    <div className="flex items-center gap-2 text-emerald-400 text-sm mb-3">
                        <CheckCircle2 className="h-4 w-4" /> Geçiş kuralları sağlanıyor
                    </div>

                    <div className="rounded-lg border border-[#25d366]/25 bg-[#25d366]/5 p-3 mb-3 space-y-2">
                        <p className="flex items-center gap-2 text-xs text-[#25d366]">
                            <MessageCircle className="h-4 w-4" />
                            Durum değişince müvekkile WhatsApp otomatik gider (onay beklemez).
                        </p>

                        {waPreview?.ok && (
                            <div className="rounded-md border border-white/10 bg-black/20 p-3">
                                <p className="text-[10px] uppercase text-slate-500 mb-1">
                                    Mesaj önizleme · {waPreview.muvekkil_adi} · {waPreview.telefon}
                                </p>
                                <pre className="text-xs text-slate-200 whitespace-pre-wrap font-sans leading-relaxed max-h-56 overflow-y-auto">
                                    {waPreview.mesaj}
                                </pre>
                            </div>
                        )}

                        {waPreview && !waPreview.ok && (
                            <p className="text-xs text-amber-300">
                                WhatsApp: {waPreview.detail || "Gönderilemez"}
                            </p>
                        )}
                    </div>
                </>
            )}
            <div className="flex gap-2">
                <button
                    type="button"
                    disabled={!target || loading || target === currentStatus}
                    onClick={() => applyTransition(false)}
                    className="h-9 px-4 rounded-md bg-[#D4AF37] text-slate-900 text-xs font-semibold disabled:opacity-40"
                >
                    Durumu uygula
                </button>
                <button
                    type="button"
                    disabled={!target || loading}
                    onClick={() => applyTransition(true)}
                    className="h-9 px-3 rounded-md border border-amber-500/40 text-amber-300 text-xs"
                    title="Kuralları yoksay (yönetici)"
                >
                    Zorla geç
                </button>
            </div>
        </section>
    );
}
